
import { Component, ChangeDetectionStrategy, inject, signal, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, AbstractControl } from '@angular/forms';
import { Router, RouterLink, ActivatedRoute } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { DataService } from '../../services/data.service';
import { CommonModule } from '@angular/common';
import { PlatformLegalModalComponent } from '../platform-legal-modal/platform-legal-modal.component';

@Component({
    selector: 'app-register',
    standalone: true,
    templateUrl: './register.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ReactiveFormsModule, RouterLink, CommonModule, PlatformLegalModalComponent],
})
export class RegisterComponent implements OnInit {
    private authService = inject(AuthService);
    private dataService = inject(DataService);
    private router: Router = inject(Router);
    private route: ActivatedRoute = inject(ActivatedRoute);

    registerForm: FormGroup;
    isLoading = signal(false);
    errorMessage = signal<string | null>(null);
    successMessage = signal<string | null>(null);

    // Invitation Token State
    token = signal<string | null>(null);
    isInvitationMode = signal(false);
    validationState = signal<'validating' | 'valid' | 'invalid'>('validating');
    validationError = signal<string | null>(null);

    // Visibility States
    showPassword = signal(false);
    showConfirmPassword = signal(false);

    // In-App Browser Detection
    isInAppBrowser = signal(false);

    // Legal Modal State
    isLegalModalOpen = signal(false);
    legalModalTab = signal<'tos' | 'privacy'>('tos');

    constructor() {
        const fb: FormBuilder = inject(FormBuilder);
        this.registerForm = fb.group({
            name: ['', Validators.required],
            email: ['', [Validators.required, Validators.email]],
            password: ['', [Validators.required, Validators.minLength(6)]],
            confirmPassword: ['', Validators.required],
            pin: [''], 
            businessType: ['CLINIC', Validators.required],
            agreeTerms: [false, Validators.requiredTrue] // NEW: Agreement Checkbox
        }, { validators: this.passwordMatchValidator });
    }

    async ngOnInit(): Promise<void> {
        this.checkInAppBrowser();
        await this.authService.prepareForNewSession();

        const errorDesc = this.route.snapshot.queryParamMap.get('error_description');
        if (errorDesc) {
            let msg = decodeURIComponent(errorDesc.replace(/\+/g, ' '));
            if (msg.includes('Database error saving new user')) {
                msg = 'เกิดข้อผิดพลาดในการบันทึกข้อมูล (อาจเกิดจากอีเมลซ้ำหรือระบบขัดข้อง) กรุณาลองใหม่อีกครั้ง';
            }
            this.errorMessage.set(msg);
        }

        let token = this.route.snapshot.queryParamMap.get('token');
        
        if (!token) {
            const urlParams = new URLSearchParams(window.location.search);
            token = urlParams.get('token');
        }

        if (token) {
            this.isInvitationMode.set(true);
            this.token.set(token);
            this.registerForm.get('pin')?.setValidators([Validators.required, Validators.pattern(/^\d{6}$/)]);
            this.registerForm.get('pin')?.updateValueAndValidity();
            // In invite mode, business type is determined by the inviting store, so hide it.
            this.registerForm.get('businessType')?.clearValidators();
            this.registerForm.get('businessType')?.updateValueAndValidity();
            
            this.validateToken(token);
        } else {
            this.isInvitationMode.set(false);
            this.registerForm.get('pin')?.clearValidators();
            this.registerForm.get('pin')?.updateValueAndValidity();
            this.validationState.set('valid');
        }
    }

    checkInAppBrowser() {
        const ua = navigator.userAgent || navigator.vendor || (window as any).opera;
        const isLine = (ua.indexOf("Line") > -1) || (ua.indexOf("FBAN") > -1) || (ua.indexOf("FBAV") > -1);
        this.isInAppBrowser.set(isLine);
    }

    closeInAppWarning() {
        this.isInAppBrowser.set(false);
    }

    togglePassword(): void {
        this.showPassword.update(v => !v);
    }

    toggleConfirmPassword(): void {
        this.showConfirmPassword.update(v => !v);
    }

    // Modal Actions
    openLegalModal(tab: 'tos' | 'privacy') {
        this.legalModalTab.set(tab);
        this.isLegalModalOpen.set(true);
    }

    closeLegalModal() {
        this.isLegalModalOpen.set(false);
    }

    async validateToken(token: string): Promise<void> {
        this.validationState.set('validating');
        try {
            const inviteDetails = await this.dataService.getInvitationDetails(token);
            if (inviteDetails && inviteDetails.email) {
                this.registerForm.patchValue({ email: inviteDetails.email });
                this.registerForm.get('email')?.disable(); 
                this.validationState.set('valid');
            } else {
                this.validationState.set('invalid');
                this.validationError.set('คำเชิญนี้ไม่ถูกต้อง หมดอายุ หรือถูกใช้งานไปแล้ว');
                this.registerForm.disable();
            }
        } catch (e) {
            this.validationState.set('invalid');
            this.validationError.set('เกิดข้อผิดพลาดในการตรวจสอบคำเชิญ');
            this.registerForm.disable();
        }
    }

    passwordMatchValidator(g: AbstractControl) {
        return g.get('password')?.value === g.get('confirmPassword')?.value
            ? null : { 'mismatch': true };
    }

    async onSubmit(): Promise<void> {
        if (this.registerForm.invalid || this.validationState() !== 'valid') {
            this.registerForm.markAllAsTouched(); // Trigger validation errors UI
            return;
        }

        this.isLoading.set(true);
        this.errorMessage.set(null);
        this.successMessage.set(null);

        const { name, password, pin, businessType } = this.registerForm.value; // Get businessType
        const email = this.registerForm.getRawValue().email; 

        // Pass businessType to signup
        const result = await this.authService.signup(email, password, name, this.token(), businessType, pin);

        if (result.success) {
            this.successMessage.set('ลงทะเบียนสำเร็จ! กรุณาล็อกอินด้วยอีเมลและรหัสผ่านเพื่อเริ่มใช้งาน (Redirecting...)');
            setTimeout(() => {
                this.router.navigate(['/login']);
            }, 3000);
        } else {
            let msg = result.error || 'An unknown error occurred during registration.';
            const lowerMsg = msg.toLowerCase();
            
            if (lowerMsg.includes('rate limit') || lowerMsg.includes('429')) {
                msg = 'ทำรายการถี่เกินไป กรุณารอประมาณ 1-2 นาทีแล้วลองใหม่ (Rate Limit Exceeded)';
            } else if (lowerMsg.includes('weak_password')) {
                msg = 'รหัสผ่านไม่ปลอดภัย กรุณาใช้รหัสผ่านที่มีความยาวอย่างน้อย 6 ตัวอักษร';
            } else if (lowerMsg.includes('already registered') || lowerMsg.includes('unique constraint')) {
                msg = 'อีเมลนี้ถูกใช้งานแล้ว กรุณาล็อกอินหรือใช้อีเมลอื่น';
            }

            this.errorMessage.set(msg);
        }

        this.isLoading.set(false);
    }

    async signUpWithGoogle(): Promise<void> {
        // Need to validate agreement first? Google sign up bypasses form, 
        // but typically user agrees by signing up. 
        // For strict compliance, we could require a checkbox before the Google button is enabled,
        // but Google Auth usually implies consent in its own flow or we handle it post-signup.
        // For simplicity here, we assume standard OAuth flow implies agreement or we ask later.
        
        this.isLoading.set(true);
        const result = await this.authService.loginWithGoogle();
        if (!result.success) {
            this.errorMessage.set(result.error || 'Google signup failed.');
            this.isLoading.set(false);
        }
    }
}
